/****************************************************
 * Module Name:  RapnetOperation.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import * as React from "react";
import { AppBar, Box, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { IoDiamondSharp } from "react-icons/io5";
import { FaUpload } from "react-icons/fa";
import PropTypes from "prop-types";
import SwipeableViews from "react-swipeable-views";
import { GiStoneBlock } from "react-icons/gi";

import NoPermission from "../common/noPermission";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";

import UploadToRapnet from "./uploadToRapnet";
import EditRemoveFromRapnet from "./editRemoveFromRapnet";
import AffectedStonesRapnet from "./affectedStonesRapnet";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 1 }}>
                    <Typography component={"span"}>{children}</Typography>
                </Box>
            )}
        </Box>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        "aria-controls": `full-width-tabpanel-${index}`,
        value: index,
    };
}

export default function RapnetOperation() {
    const theme = useTheme();
    const RAPNET_UPLOAD = Constants.USER_PERMISSION.filter((iPermission) =>
        (iPermission.permissionid === Constants.PERMISSIONS.OPERATION_RAPNET_UPLOAD));
    const RAPNET_EDIT_REMOVE = Constants.USER_PERMISSION.filter((iPermission) =>
        (iPermission.permissionid === Constants.PERMISSIONS.OPERATION_RAPNET_EDIT_REMOVE));
    const RAPNET_AFFECTED_STONE = Constants.USER_PERMISSION.filter((iPermission) =>
        (iPermission.permissionid === Constants.PERMISSIONS.OPERATION_RAPNET_AFFECTED_STONE));

    const [value, setValue] = React.useState(
        Utility.isReadAllowed(RAPNET_UPLOAD) ? 0 :
            Utility.isReadAllowed(RAPNET_EDIT_REMOVE) ? 1 :
                Utility.isReadAllowed(RAPNET_AFFECTED_STONE) ? 2 : 3
    );

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleChangeIndex = (index) => {
        setValue(index);
    };

    return (
        value === 3 ? <NoPermission /> : (
            <Box sx={{ bgcolor: "background.paper", width: "100%", padding: "10px" }}>
                <AppBar position="static" sx={{ bgcolor: "#03a9f4", height: 50 }}>
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        indicatorColor="secondary"
                        textColor="inherit"
                        variant="fullWidth"
                        aria-label="full width tabs example"
                        sx={{
                            "& .css-1vw5i9y-MuiButtonBase-root-MuiTab-root": {
                                color: "#212121",
                                fontWeight: "bold",
                                textAlign: 'center'
                            },
                            "& .MuiTab-root": {
                                paddingBottom: 4
                            },
                            "& .Mui-selected": {
                                bgcolor: "#0277bd !important",
                                color: "#fafafa !important",
                                fontWeight: "bold !important",
                                paddingBottom: 4
                            }
                        }}
                        TabIndicatorProps={{ style: { background: "#0277bd" } }}
                    >
                        {Utility.isReadAllowed(RAPNET_UPLOAD) ? (
                            <Tab
                                icon={<FaUpload size="1.5em" />}
                                iconPosition="start"
                                label="Upload To Rapnet"
                                {...a11yProps(0)}
                            />
                        ) : null}

                        {Utility.isReadAllowed(RAPNET_EDIT_REMOVE) ? (
                            <Tab
                                icon={<IoDiamondSharp size="2em" />}
                                iconPosition="start"
                                label="Edit/Remove from Rapnet"
                                {...a11yProps(1)}
                            />
                        ) : null}

                        {Utility.isReadAllowed(RAPNET_AFFECTED_STONE) ? (
                            <Tab
                                icon={<GiStoneBlock size="2em" />}
                                iconPosition="start"
                                label="Affected Stones"
                                {...a11yProps(2)}
                            />
                        ) : null}
                    </Tabs>
                </AppBar>
                <SwipeableViews
                    axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                    index={value}
                    onChangeIndex={handleChangeIndex}
                >
                    {Utility.isReadAllowed(RAPNET_UPLOAD) ? (
                        <TabPanel value={value} index={0}>
                            <UploadToRapnet />
                        </TabPanel>
                    ) : null}

                    {Utility.isReadAllowed(RAPNET_EDIT_REMOVE) ? (
                        <TabPanel value={value} index={1}>
                            <EditRemoveFromRapnet />
                        </TabPanel>
                    ) : null}

                    {Utility.isReadAllowed(RAPNET_AFFECTED_STONE) ? (
                        <TabPanel value={value} index={2}>
                            <AffectedStonesRapnet />
                        </TabPanel>
                    ) : null}
                </SwipeableViews>
            </Box >
        )
    )
}